import React from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Route, Switch } from 'react-router-dom';
import { i18nIntl, isNotEmpty } from '../../lib/ep.js';
import { orderBy as _orderBy, isEmpty } from 'lodash';

import ActionLink from '../builder/ActionLink';
import BuilderSidebar from '../builder/BuilderSidebar';
import ButtonCollection from '../builder/ButtonCollection';
import Checkbox from '../builder/Checkbox';
import ColorPicker from '../builder/ColorPicker';
import DisabledArea from '../builder/DisabledArea';
import { AutoPostImageUploader } from '../builder/ImageUploader';
import Input from '../builder/Input';
import Message from '../builder/Message';
import Model from '../builder/Model';
import { NavigationTabsBuilder } from '../builder/NavigationTabs';
import Range from '../builder/Range';
import RichEditor from '../builder/RichEditor';
import Select from '../builder/Select';

import transferPlaceholder from '../../hoc/transferPlaceholder';
import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
import Tooltip from '../builder/Tooltip.js';
import StageFinalMessages, { useFinalPage } from '../games/StageFinalMessages.js';
import { stage_categories } from '../../data/bootstrap_stages.js';

const InputPlaceHolder = injectIntl(transferPlaceholder(Input));
const RichPlaceHolder = injectIntl(transferPlaceholder(RichEditor));

const TriviaSidebar = ({ state, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stage = props?.stage ?? {};
	const total_questions = props?.total_questions ?? 0;
	const answers = props.user;
	const questions = props.questions;
	const section_main_path = props.section_main_path;

	const [ actual_state, setFinalCase ] = useFinalPage(stage?.final_messages);
	stage.display_status = stage?.display_status ?? 'success';

	let BuilderSidebar_props = {};
	if((props?.option ?? 'settings') != 'testreact') {
		BuilderSidebar_props.onSave = props.saveStage;
	}

	return (
		<BuilderSidebar {...BuilderSidebar_props}>
			<Switch>
				<Route exact={true} path={`/${section_main_path}/settings`} render={(route) => {
					const gamemode = [
						{ k:'', v:i18n("Pop quiz") },
						{ k:'allright', v:i18n("Repeat round quiz") },
						{ k:'suddendeath', v:i18n("Sudden death") },
					];

					const max_points_per_questions = [];
					if(stage.quiz_mode != 'allright') {
						max_points_per_questions.push({ k:'100', v:"100 (" + i18n("default") + ")" });
						max_points_per_questions.push({ k:'10', v:"10" });
						max_points_per_questions.push({ k:'1', v:"1" });
					} else {
						max_points_per_questions.push({ k:'100', v:i18n("Descending") });
					}

					return (
						<>
							<NavigationTabsBuilder active={route.location.pathname} sections={props.sections} />

							<article>
								<Model model={stage} change={props.changeStage}>
									<Select
										name="quiz_mode"
										values={gamemode}
										_class={{ first:true, size:50 }}
									>Game mode</Select>
								</Model>

								<Message
									_class={{ other:'not_in_bottom' }}
								>
									{(['allright'].includes(stage.quiz_mode)) && (
										<p>{i18n("The game ends when you have answered all the questions correctly. You have multiple rounds and in each one, you will see the questions that you got wrong in the previous round.")}<br />{i18n("In the first round, the users earn 100 points for each correct answer, in the second round 80 points, in the third 60 points, in the fourth 40 points and from the fifth onward, 1 point.")}</p>
									)}

									{(['suddendeath'].includes(stage.quiz_mode)) && (
										<p>{i18n("The game ends when you get a question wrong. Get as many questions correct as possible.")}<br />{i18n("The users earn 100 points for each correct answer.")}</p>
									)}

									{(!['allright', 'suddendeath'].includes(stage.quiz_mode)) && (
										<p>{i18n("The game ends when you have answered all the questions. Get as many questions right as fast as possible.")}<br />{i18n("The users earn 100 points for each correct answer.")}</p>
									)}
								</Message>

								<Model model={stage} change={props.changeStage}>
									<Checkbox
										name="random_questions"
									>Show questions randomly</Checkbox>

									<Checkbox
										name="random_answers"
									>Show answers randomly</Checkbox>

									{(stage.quiz_mode != 'allright') && (
										<Checkbox
											name="show_correct_answer"
										>Show right answer in case the participant fails</Checkbox>
									)}

									<Range
										key="time_question"
										min={5}
										max={120}
										step={5}
										name="time_question"
										value={stage?.time_question ?? 10}
										label_props={{
											addition:"In seconds",
										}}
									>Time limit for each question</Range>

									<Checkbox
										name="display_string_timer"
									>Display a numeric countdown timer</Checkbox>

									{(total_questions > 1) && (
										<Range
											key="max_questions"
											min={1}
											max={props?.total_questions ?? 1}
											step={1}
											name="max_questions"
											value={stage?.max_questions ?? props.total_questions}
										>Number of questions to display</Range>
									)}

									<Checkbox
										name="silence"
									>Disable sounds</Checkbox>
								</Model>
							</article>
						</>
					)
				}} />

				<Route exact={true} path={`/${section_main_path}/instructions`} render={(route) => {
					const start_button_label = i18n("Label for '%s' button").replace('%s', i18n("Start"));

					return (
						<>
							<NavigationTabsBuilder
								active={route.location.pathname}
								sections={props.sections}
							/>

							<article>
								<Model model={stage} change={props.changeStage}>
									<Checkbox
										name="instructions_disabled"
										_class={{ first:true }}
									>Disable instructions</Checkbox>
								</Model>

								{((stage?.instructions_disabled ?? false) === false) && (
									<Model model={stage} change={props.changeStage}>
										<AutoPostImageUploader
											auto_url={`/promotions/auto_img/${stage.promotion_id}`}
											auto_type='stages'
											name="instructions_img"
											image={stage.full_instructions_img}
											display_custom_dragging_instructions="Upload the image"
											small_input={true}
											_size={{ fit:'auto' }}
										>Image</AutoPostImageUploader>

										<RichEditor
											name="instructions_description"
											has_emojis={true}
										>Description</RichEditor>

										<InputPlaceHolder
											name="instructions_button"
											placeholder={i18n("Start")}
											autotranslate={false}
										>{start_button_label}</InputPlaceHolder>
									</Model>
								)}
							</article>
						</>
					)
				}} />

				<Route exact={true} path={`/${section_main_path}/gamescore`} render={(route) => {
					let final_messages = stage?.final_messages ?? false;

					if(final_messages === false) {
						final_messages = [{
							id:'success',
							type:'final_message',
							text:stage?.end_score ?? `<h1>${i18n("Thank you for playing!")}</h1>`,
							color:stage?.color_score ?? 'rgba(120, 195, 0, 1)',
						}, {
							id:'fail',
							type:'final_message',
							text:`<h1>${i18n("Game over!")}</h1>`,
							color:'rgba(215, 35, 60, 1)',
						}];

						if(isNotEmpty(stage, 'game_final_image_ok')) {
							final_messages[0].image = stage.game_final_image_ok;
							final_messages[0].full_image = stage.full_game_final_image_ok;
						}
					}
					const is_timed_quiz = (stage_categories.ID_TIMED_CATEGORY.id == stage.type) ? true : false;
					const is_mode_standard = !['suddendeath','allright'].includes(stage?.quiz_mode ?? '') ? true : false;
					return (
						<>
							<NavigationTabsBuilder
								active={route.location.pathname}
								sections={props.sections}
							/>
 
							<article>
								{(is_timed_quiz && is_mode_standard) && (
									<Range
										key="quiz_answered_correctly"
										min={0}
										max={stage?.max_questions ?? total_questions}
										step={1}
										name="quiz_answered_correctly"
										value={stage?.quiz_answered_correctly ?? (stage?.max_questions ?? total_questions)}
										model={stage} 
										change={props.changeStage}
									>How many questions must be answered correctly?</Range>
								)}
								<StageFinalMessages
									stage={stage}
									final_messages={final_messages}
									total_questions={total_questions}
									fields={['color', 'image']}
									exclude={['out_of_time', 'out_of_moves']}
									onOpen={(id) => {
										if(!id) return;
										setFinalCase(id);
										if(props.onChangeFinalCase != undefined) props.onChangeFinalCase(id);
									}}
									change={(v) => {
										props.changeStage('final_messages', v)
									}}
								/>

								<Model model={stage} change={props.changeStage}>
									{/*<AutoPostImageUploader
										auto_url={`/promotions/auto_img/${stage.promotion_id}`}
										auto_type='stages'
										image={stage.full_game_final_image_ok}
										changeMulti={props.changeMulti}
										name="game_final_image_ok"
										small_input={true}
										_size={{ fit:'auto' }}
										_class={{first:true}}
									>Image</AutoPostImageUploader>

									<RichPlaceHolder
										name="end_score"
										placeholder={`<h1>${i18n("Thank you for playing!")}</h1>`}
										has_emojis={true}
									>Feedback message text</RichPlaceHolder>*/}

									<Checkbox
										name="hide_score_resumee"
									>Hide score summary</Checkbox>

									<Checkbox
										name="override_promo_styles"
										value={stage?.override_promo_styles ?? false}
										label_props={{
											tooltip:<Tooltip>Configure the game result screens with specific background colors for each outcome. For example, use a green background to show the game was completed successfully and a red background when it wasn't. Enable this option if you prefer not to use different colors based on the game's result and instead apply the promotion's general color scheme.</Tooltip>,
										}}
									>Apply the promotion's general colors</Checkbox>

									{/*((stage?.override_promo_styles ?? false) === false) && (
										<ColorPicker
											key="color_score"
											name="color_score"
											color={stage?.color_score ?? 'rgba(var(--ep-green-color), 1)'}
											_class={{size:70}}
										>Color</ColorPicker>
									)*/}
								</Model>
							</article>

							<DisabledArea
								disabled={(stage.quiz_mode == 'allright') ? true : false}
								message="Summary is not enabled with this game mode"
							>
								<article class="gui_not_in_top_huge">
									<BuilderSubSectionTitle
										title="Game summary"
										icon='knk-list'
									/>

									<Model model={stage} change={props.changeStage}>
										<Checkbox
											name="display_summary"
											model={stage}
											change={props.changeStage}
											value={(stage.quiz_mode == 'allright') ? false : stage.display_summary}
										>Show game summary</Checkbox>

										{(stage.display_summary) && (
											<RichPlaceHolder
												name="end_summary"
												placeholder="These are your answers"
												has_emojis={true}
											>Introduction text</RichPlaceHolder>
										)}

										{(stage.display_summary) && (
											<Checkbox
												name="display_right_or_wrong_summary"
											>Show right or wrong answers</Checkbox>
										)}
									</Model>
								</article>
							</DisabledArea>
						</>
					)
				}} />

				<Route exact={true} path={`/${section_main_path}/testreact`} render={(route) => {
					let rounds = [];
					$.each(answers, (question_id, answer) => {
						const aid = Object.keys(answer)[0];
						const avalue = Object.values(answer)[0];

						const qid = (stage.quiz_mode == 'allright') ? avalue.question_id : question_id;

						const search_question = questions.find((question) => (question.Question.id == qid));
						const search_answer = (search_question) ? (search_question.Answer).find((question) => (question.id == aid)) : false;

						const result = Object.values(answer)[0];
						if(search_question) result.q = search_question.Question.question;
						result.a = search_answer ? search_answer.answer : i18n("Not answered");

						const round = (stage.quiz_mode == 'allright') ? result.round : 0;
						if(rounds[round] == undefined) rounds[round] = [];
						rounds[round].push(result);
					});

					if(stage.quiz_mode != 'allright') {
						rounds[0] = _orderBy(rounds[0], ['current'], ['asc']);
					}

					const allright_score_rounds = [100, 80, 60, 40, 20, 1];

					return (
						<>
							<NavigationTabsBuilder
								active={route.location.pathname}
								sections={props.sections}
							/>

							{rounds.length > 0 && rounds.map((round, r) => {
								if(round.length < 1) return null;

								return (
									<article key={`round_${r}`} class={`quiz_test_summary${(r > 0) ? ' gui_not_in_top_huge' : ''}`}>
										{(stage.quiz_mode == 'allright') && (
											<BuilderSubSectionTitle
												title={`${i18n("Round")} ${r + 1}`}
												icon='knk-list'
												title_actions={<span style={{ paddingTop:'3px' }}>[<FormattedMessage id="{score} points per answer" values={{score:allright_score_rounds[r]}} />]</span>}
												bordered={false}
											/>
										)}

										<ul class="gui_sortable_list gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap">
											{round.map((q, i) => {
												const icon = (q.correct) ? 'knk-on greenIcon' : 'knk-off redIcon';

												return (
													<li key={`aq_${i}`} class={`gui_sortable_list_item gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap gui_flex_align_center ${(q.correct) ? 'is_correct' : 'is_incorrect'}`}>
														<div class="item_header">
															<div class="item_title_container">
																<ActionLink
																	icon={icon}
																	_class={{ other:'item_title cutTextOnOverflow gui_remove_pointer_events' }}
																	autotranslate={false}
																><strong>{q.q}</strong></ActionLink>

																<span class="answer">{q.a}</span>
															</div>

															<ButtonCollection>
																<p>{q.points}</p>
															</ButtonCollection>
														</div>
													</li>
												)
											})}
										</ul>
									</article>
								)
							})}
						</>
					)
				}} />
			</Switch>
		</BuilderSidebar>
	)
}

export default injectIntl(TriviaSidebar);